import { tr } from "../../i18n";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api, apiError } from "../../auth/AuthContext";
import { Badge, statusTone, Card, fmtDateTime } from "../../components/portal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
const SERVICES = [{
  type: "ERI",
  title: "ERI",
  sub: "Ethical Readiness Index",
  desc: "Free initial AI readiness assessment.",
  cta: "Start ERI"
}, {
  type: "INDIVIDUAL",
  title: "Single-Entity",
  sub: "Single-Entity AI Assessment",
  desc: "For organizations with 1 to 10 legal entities.",
  cta: "Request Certification"
}, {
  type: "GROUP",
  title: "Group-Wide",
  sub: "Group-Wide AI Assessment",
  desc: "For organizations with more than 10 entities or large/complex structures.",
  cta: "Request Group-Wide Assessment"
}, {
  type: "RECERTIFICATION",
  title: "Annual Recertification",
  sub: "Annual ESAIA Recertification",
  desc: "For existing ESAIA-certified organizations.",
  cta: "Request Recertification"
}];
export default function RequestService() {
  const {
    lang = "en"
  } = useParams();
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/client/service-requests").then(r => setRequests(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/client/service-requests", {
        service_type: selected.type,
        message
      });
      toast.success(tr("Your service request has been submitted."));
      setSelected(null);
      setMessage("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };
  return <div className="mx-auto max-w-5xl" data-testid="request-service-page">
      <h1 className="text-2xl font-semibold tracking-tight text-esaia-ink sm:text-3xl">{tr("Request an ESAIA Service")}</h1>
      <p className="mt-1 text-sm text-esaia-muted">{tr("Select a service below. ESAIA will review your request and open an assessment.")}</p>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
        {SERVICES.map(s => <div key={s.type} className="flex flex-col border border-esaia-line bg-white p-7" data-testid={`service-card-${s.type.toLowerCase()}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-blue">{tr(s.sub)}</p>
            <h2 className="mt-2 text-lg font-semibold text-esaia-ink">{tr(s.title)}</h2>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-esaia-muted">{tr(s.desc)}</p>
            {s.type === "ERI" ? <Link to={`/${lang}/eri/assessment`} data-testid="request-eri-button" className="group mt-6 inline-flex items-center justify-center gap-2 border border-esaia-blue bg-esaia-blue px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-esaia-blue">
                {tr(s.cta)}
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link> : <button onClick={() => setSelected(s)} data-testid={`request-${s.type.toLowerCase()}-button`} className="group mt-6 inline-flex items-center justify-center gap-2 border border-esaia-blue bg-esaia-blue px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-esaia-blue">
                {tr(s.cta)}
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </button>}
          </div>)}
      </div>

      <div className="mt-10">
        <Card title={tr("My Service Requests")} testid="my-service-requests">
          {requests.length === 0 ? <p className="text-sm text-esaia-subtle">{tr("No service requests yet.")}</p> : <ul className="divide-y divide-esaia-line">
              {requests.map(r => <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-testid={`request-row-${r.id}`}>
                  <div>
                    <p className="text-sm font-medium text-esaia-ink">{tr(r.service_label)}</p>
                    <p className="text-xs text-esaia-subtle">{fmtDateTime(r.created_at)}</p>
                  </div>
                  <Badge tone={statusTone(r.status)}>{tr(r.status)}</Badge>
                </li>)}
            </ul>}
        </Card>
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="sm:max-w-md" data-testid="service-request-dialog">
          <DialogHeader>
            <DialogTitle className="text-esaia-ink">{selected?.cta}</DialogTitle>
            <DialogDescription className="text-esaia-muted">
              {selected?.sub}{tr(". Add a short message for the ESAIA team (optional).")}{" "}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <textarea rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder={tr("Context, number of entities, timelines...")} data-testid="service-request-message" className="w-full border border-esaia-line bg-white px-3 py-2.5 text-sm text-esaia-ink placeholder:text-esaia-subtle focus:border-esaia-blue focus:outline-none" />
            <button type="submit" disabled={loading} data-testid="service-request-submit" className="w-full bg-esaia-blue px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark disabled:opacity-60">
              {loading ? "Submitting..." : "Submit Request"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>;
}
